import { useState, useEffect, useRef, useCallback } from "react";
import axios, { API, TOKEN_KEY } from "../utils/api";

/** Sends the save with `keepalive: true` so it survives a tab close/refresh, unlike a normal axios/XHR call which can get cancelled mid-flight. */
function flushSave(payload) {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/api/user-data`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch { }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Retries an async request instead of silently giving up on the first network hiccup — a slow or
 * flaky connection shouldn't mean a save just vanishes with no sign anything went wrong.
 * Returns true once it actually succeeds, false if all attempts failed.
 *
 * The backoff schedule spans ~60s total, not just a couple of seconds — Render's free tier
 * spins down when idle and can take 10-30s+ to wake back up on the first request after a gap,
 * and that's a normal wake-up, not a real failure. A short retry window would flag that as
 * "failed" and show a scary banner right as the save was about to succeed on its own.
 */
async function requestWithRetry(fn, attempts = 6, delays = [2000, 4000, 8000, 14000, 20000]) {
  for (let i = 0; i < attempts; i++) {
    try {
      await fn();
      return true;
    } catch {
      if (i < attempts - 1) await sleep(delays[i] ?? 20000);
    }
  }
  return false;
}

const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;

function readLocal(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)); } catch { return fallback; }
}

/**
 * Owns watchlist, investments, and the prediction log — persisted to MongoDB (via /api/user-data)
 * with localStorage as an instant-load cache. The server's data wins on initial load *unless*
 * the user has already started editing before that response arrives, in which case their local
 * edit wins instead (see userEditedRef below) — otherwise a slow initial load (e.g. a cold-started
 * Render backend) could silently overwrite a fresh edit with stale server data.
 */
export function useUserData() {
  const [watchlist, setWatchlist]     = useState(() => readLocal("wl", []));
  const [investments, setInvestments] = useState(() => readLocal("ts_investments", {}));
  const [predLog, setPredLog]         = useState(() => readLocal("ts_pred_log", []));
  const [alerts, setAlerts]           = useState(() => readLocal("ts_alerts", {}));
  const [scannedStocks, setScannedStocks] = useState(() => readLocal("ts_scanned_stocks", []));
  const [lastScan, setLastScan]      = useState(() => {
    const v = readLocal("ts_last_scan", null);
    return v ? new Date(v) : null;
  });
  const [syncFailed, setSyncFailed] = useState(false);
  const loadedRef = useRef(false);
  const saveTimer = useRef(null);
  // The initial GET can take a while (Render's free tier spins down when idle and can take
  // 10-30s+ to wake up). If the user starts editing before it resolves, applying the server's
  // (older) response would silently stomp their fresh edit. Once they've touched anything,
  // stop letting this one-time initial load overwrite state — local edits win from then on.
  const userEditedRef = useRef(false);
  const markEdited = useCallback(() => { userEditedRef.current = true; }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get("/api/user-data")
      .then((res) => {
        if (cancelled || userEditedRef.current) return;
        const { watchlist: wl, investments: inv, predictions: preds, alerts: al, scannedStocks: scanned, lastScan: last } = res.data || {};
        if (wl) setWatchlist(wl);
        if (inv) setInvestments(inv);
        if (preds) setPredLog(preds);
        if (al) setAlerts(al);
        if (scanned && scanned.length) setScannedStocks(scanned);
        if (last) setLastScan(new Date(last));
      })
      .catch(() => {
        // Server/DB unreachable — keep working off the localStorage cache.
      })
      .finally(() => { if (!cancelled) loadedRef.current = true; });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { try { localStorage.setItem("wl", JSON.stringify(watchlist)); } catch { } }, [watchlist]);
  useEffect(() => { try { localStorage.setItem("ts_investments", JSON.stringify(investments)); } catch { } }, [investments]);
  useEffect(() => { try { localStorage.setItem("ts_pred_log", JSON.stringify(predLog)); } catch { } }, [predLog]);
  useEffect(() => { try { localStorage.setItem("ts_alerts", JSON.stringify(alerts)); } catch { } }, [alerts]);
  useEffect(() => { try { localStorage.setItem("ts_scanned_stocks", JSON.stringify(scannedStocks)); } catch { } }, [scannedStocks]);
  useEffect(() => { try { localStorage.setItem("ts_last_scan", JSON.stringify(lastScan)); } catch { } }, [lastScan]);

  const latestPayloadRef = useRef(null);
  const saveTokenRef = useRef(0);

  // watchlist/investments are deliberately NOT in this payload — see the targeted
  // watchlist/investment endpoints below for why.
  useEffect(() => {
    if (!loadedRef.current) return;
    const payload = { predictions: predLog, alerts, scannedStocks, lastScan };
    latestPayloadRef.current = payload;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const token = ++saveTokenRef.current;
      requestWithRetry(() => axios.put("/api/user-data", payload)).then((ok) => {
        if (saveTokenRef.current !== token) return; // a newer edit already superseded this save
        setSyncFailed(!ok);
      });
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [predLog, alerts, scannedStocks, lastScan]);

  const retrySync = useCallback(() => {
    if (!latestPayloadRef.current) return;
    const token = ++saveTokenRef.current;
    requestWithRetry(() => axios.put("/api/user-data", latestPayloadRef.current)).then((ok) => {
      if (saveTokenRef.current !== token) return;
      setSyncFailed(!ok);
    });
  }, []);

  // If the tab is closed, refreshed, or backgrounded before the debounce above fires,
  // the pending save would otherwise be silently lost. Flush it immediately instead.
  useEffect(() => {
    const flush = () => {
      if (!loadedRef.current || !latestPayloadRef.current) return;
      clearTimeout(saveTimer.current);
      flushSave(latestPayloadRef.current);
    };
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // watchlist/investments save immediately as targeted, atomic MongoDB operations (addToSet/pull/
  // set/unset on a single field) rather than going through the bulk debounced snapshot-overwrite
  // above. That bulk approach breaks with more than one tab or device open on the same account:
  // a stale tab resaving its own outdated full snapshot would silently wipe out an edit a
  // different, more current tab just made. A targeted mutation can't clobber unrelated fields no
  // matter which tab sends it or in what order they arrive.
  const toggleWL = useCallback((sym) => {
    markEdited();
    const removing = watchlist.includes(sym);
    setWatchlist((p) => removing ? p.filter((s) => s !== sym) : [...p, sym]);
    const token = ++saveTokenRef.current;
    // Un-starring removes the stock from the Watchlist tab entirely, including any tracked
    // investment — otherwise it silently stays in the list (still keyed by `investments`) and
    // un-starring looks like it "did nothing" even though the star itself toggled correctly.
    if (removing && investments[sym]) {
      setInvestments((p) => { const n = { ...p }; delete n[sym]; return n; });
    }
    const reqs = removing
      ? [() => axios.delete(`/api/user-data/watchlist/${encodeURIComponent(sym)}`), ...(investments[sym] ? [() => axios.delete(`/api/user-data/investments/${encodeURIComponent(sym)}`)] : [])]
      : [() => axios.post("/api/user-data/watchlist", { symbol: sym })];
    Promise.all(reqs.map((req) => requestWithRetry(req))).then((results) => {
      if (saveTokenRef.current !== token) return;
      setSyncFailed(results.some((ok) => !ok));
    });
  }, [markEdited, watchlist, investments]);

  const addInvestment = useCallback((symbol, entryPrice) => {
    const price = Number(entryPrice);
    if (!price || price <= 0) return;
    markEdited();
    setInvestments((p) => ({ ...p, [symbol]: { entryPrice: price, addedAt: Date.now() } }));
    const token = ++saveTokenRef.current;
    requestWithRetry(() => axios.put(`/api/user-data/investments/${encodeURIComponent(symbol)}`, { entryPrice: price })).then((ok) => {
      if (saveTokenRef.current !== token) return;
      setSyncFailed(!ok);
    });
  }, [markEdited]);

  const removeInvestment = useCallback((symbol) => {
    markEdited();
    setInvestments((p) => { const n = { ...p }; delete n[symbol]; return n; });
    const token = ++saveTokenRef.current;
    requestWithRetry(() => axios.delete(`/api/user-data/investments/${encodeURIComponent(symbol)}`)).then((ok) => {
      if (saveTokenRef.current !== token) return;
      setSyncFailed(!ok);
    });
  }, [markEdited]);

  const setAlertsTracked = useCallback((updater) => { markEdited(); setAlerts(updater); }, [markEdited]);
  const setScannedStocksTracked = useCallback((updater) => { markEdited(); setScannedStocks(updater); }, [markEdited]);
  const setLastScanTracked = useCallback((updater) => { markEdited(); setLastScan(updater); }, [markEdited]);

  const updatePredictions = useCallback((stocks) => {
    markEdited();
    const now = Date.now();
    const priceMap = {};
    stocks.forEach((s) => { priceMap[s.symbol] = s.price; });

    setPredLog((prev) => {
      const resolved = prev.map((p) => {
        if (p.status !== "pending") return p;
        const cur = priceMap[p.symbol];
        if (!cur || now - p.timestamp < FIVE_DAYS_MS) return p;
        const won = (p.bias === "BULLISH" && cur > p.price) || (p.bias === "BEARISH" && cur < p.price);
        const ret = ((cur - p.price) / p.price) * 100 * (p.bias === "BEARISH" ? -1 : 1);
        return { ...p, status: won ? "win" : "loss", resolvedPrice: cur, resolvedDate: new Date().toLocaleDateString(), returnPct: +ret.toFixed(2) };
      });
      const pendingSyms = new Set(resolved.filter((p) => p.status === "pending").map((p) => p.symbol));
      const newPreds = stocks
        .filter((s) => s.tomorrowBias !== "NEUTRAL" && !pendingSyms.has(s.symbol))
        .map((s) => ({ id: `${s.symbol}_${now}`, symbol: s.symbol, name: s.name, bias: s.tomorrowBias, price: s.price, score: s.score, timestamp: now, date: new Date().toLocaleDateString(), status: "pending" }));
      return [...resolved, ...newPreds].slice(-300);
    });
  }, [markEdited]);

  return {
    watchlist, toggleWL,
    investments, addInvestment, removeInvestment,
    predLog, updatePredictions,
    alerts, setAlerts: setAlertsTracked,
    scannedStocks, setScannedStocks: setScannedStocksTracked,
    lastScan, setLastScan: setLastScanTracked,
    syncFailed, retrySync,
  };
}
